import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import Swal from 'sweetalert2';
const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentSlides, setCurrentSlides] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDateInputs, setShowDateInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { authToken } = useContext(AuthContext);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get('https://vasstrinfo.com/api/orders/', {
          headers: {
            Authorization: `Bearer ${authToken?.access}`,
          },
        });

        const ordersArray = response.data.orders || [];
        console.log(response.data)
        setOrders(ordersArray);

        const productIds = [...new Set(ordersArray.map(order => order.productid))];
        const mockProducts = productIds.map(id => ({
          id,
          img1: `https://d3qvi06vol9hrb.cloudfront.net/images/product${id}-1.jpg`,
          img2: `https://d3qvi06vol9hrb.cloudfront.net/images/product${id}-2.jpg`,
          img3: `https://d3qvi06vol9hrb.cloudfront.net/images/product${id}-3.jpg`,
        }));
        setProducts(mockProducts);

        const initialSlides = {};
        ordersArray.forEach((_, index) => {
          initialSlides[index] = 1;
        });
        setCurrentSlides(initialSlides);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [authToken]);

  const plusSlides = (n, index) => {
    const current = currentSlides[index] || 1;
    const maxSlides = 3;
    let next = current + n;
    if (next > maxSlides) next = 1;
    if (next < 1) next = maxSlides;

    setCurrentSlides(prev => ({
      ...prev,
      [index]: next,
    }));
  };

  const toggleOverlay = (order) => {
    setSelectedOrder(order);
  };

  const toggleDateInput = (orderId, field) => {
    setShowDateInputs(prev => ({
      ...prev,
      [`${orderId}-${field}`]: !prev[`${orderId}-${field}`],
    }));
  };

  const handleStatusSubmit = async (e, orderId) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const payload = {
      started_order: formData.get('started_order') === 'on',
      finished_order: formData.get('finished_order') === 'on',
      delivered_order: formData.get('delivered_order') === 'on',
    };

    if (formData.get('finished_order_time')) {
      payload.finished_order_datetime = formData.get('finished_order_time');
    }
    if (formData.get('delivered_order_time')) {
      payload.delivered_order_datetime = formData.get('delivered_order_time');
    }

    try {
      await axios.post(`https://vasstrinfo.com/api/update-received/${orderId}/`, payload, {
        headers: {
          Authorization: `Bearer ${authToken.access}`,
        },
      });
      Swal.fire({
        title: "Good job!",
        text: "Order status updated!",
        icon: "success"
      });
    } catch (err) {
      console.error('Error updating order:', err);
      Swal.fire({
        title: "Something Went Wrong!",
        text: "Error updating order status",
        icon: "warning"
      });
    }
  };

  if (loading) return <div className="text-center text-lg text-gray-600">Loading...</div>;
  if (error) return <div className="text-center text-lg text-red-600">Error: {error}</div>;

  return (
    <Base
      Content={
        <div className="min-h-screen bg-white font-sans">
          <div className="text-center py-6">
            <h1 className="text-2xl md:text-3xl font-bold text-orange-600 mb-2">
              Orders Received From Customers
            </h1>
            <div className="flex items-center justify-center my-4">
              <div className="flex-1 h-px bg-black"></div>
              <div className="flex mx-2 md:mx-4">
                {[...Array(7)].map((_, i) => (
                  <div key={i} className="w-3 h-3 bg-black rounded-full mx-1"></div>
                ))}
              </div>
              <div className="flex-1 h-px bg-black"></div>
            </div>
          </div>

          {orders.length === 0 ? (
            <p className="text-center text-lg">No orders found.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 md:px-8 pb-12">
              {orders.map((order, index) => {
                const product = products.find(p => p.id === order.productid);
                const images = [product?.img1, product?.img2, product?.img3].filter(Boolean);

                return (
                  <div key={order.id} className="bg-white rounded-lg shadow-md border p-4">
                    <div className="relative h-64 mb-4 rounded-lg overflow-hidden">
                      {images.map((src, i) => (
                        <div
                          key={i}
                          className={`absolute inset-0 transition-opacity duration-500 ${currentSlides[index] === i + 1 ? 'opacity-100' : 'opacity-0'
                            }`}
                        >
                          <img src={src} alt={`Product ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                      <button
                        onClick={() => plusSlides(-1, index)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-30 text-white p-1 rounded-full"
                      >
                        &lt;
                      </button>
                      <button
                        onClick={() => plusSlides(1, index)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-30 text-white p-1 rounded-full"
                      >
                        &gt;
                      </button>
                    </div>

                    <form onSubmit={(e) => handleStatusSubmit(e, order.order_id)} className="space-y-3">
                      <h3 className="text-lg font-semibold truncate">Order ID: {order.order_id}</h3>

                      {['started', 'finished', 'delivered'].map((stage) => (
                        <div key={stage} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`${stage}Order${order.id}`}
                            name={`${stage}_order`}
                            defaultChecked={!!order.status[`${stage}_order`]}
                            className="w-4 h-4"
                          />
                          <label htmlFor={`${stage}Order${order.id}`} className="text-sm">
                            {stage.charAt(0).toUpperCase() + stage.slice(1)} order
                          </label>
                          {order.status[`${stage}_order`] && (
                            <>
                              <span className="text-xs text-green-600 ml-1">
                                on {new Date(order.status[`${stage}_order`]).toLocaleString()}
                              </span>
                              {/* <button
                                type="button"
                                onClick={() => toggleDateInput(order.id, stage)}
                                className="ml-1 text-xs bg-gray-100 px-1 rounded"
                              >
                                Edit
                              </button> */}
                              {showDateInputs[`${order.id}-${stage}`] && (
                                <input
                                  type="datetime-local"
                                  className="block mt-2"
                                  name={`${stage}_order_time`}
                                />
                              )}
                            </>
                          )}
                        </div>
                      ))}

                      <div className="flex justify-end gap-3 mt-4">
                        <button
                          type="button"
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                          onClick={() => toggleOverlay(order)}
                        >
                          View Info
                        </button>
                        <button type="submit" className="px-3 py-1 bg-orange-600 text-white rounded text-sm">
                          Submit
                        </button>
                      </div>
                    </form>
                  </div>
                );
              })}

              {selectedOrder && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                  <div className="bg-white p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-2xl font-bold text-orange-600">{selectedOrder.productname}</h3>
                      <button
                        onClick={() => setSelectedOrder(null)}
                        className="text-3xl text-gray-500 hover:text-gray-700 transition-colors"
                        aria-label="Close modal"
                      >
                        &times;
                      </button>
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-col lg:flex-row gap-8">
                      {/* Image Gallery - Left Side */}
                      <div className="lg:w-2/5 xl:w-1/3">
                        {(() => {
                          const product = products.find(p => p.id === selectedOrder.productid);
                          const images = [product?.img1, product?.img2, product?.img3].filter(Boolean);
                          const overlayIndex = orders.findIndex(o => o.id === selectedOrder.id);

                          return (
                            <div className="relative">
                              {/* Main Image */}
                              <div className="relative w-full aspect-square mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                                {images.length > 0 ? (
                                  <>
                                    {images.map((src, i) => (
                                      <div
                                        key={i}
                                        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${currentSlides[overlayIndex] === i + 1 ? 'opacity-100' : 'opacity-0'
                                          }`}
                                      >
                                        <img
                                          src={src}
                                          alt={`Product ${i + 1}`}
                                          className="w-full h-full object-contain p-4"
                                        />
                                      </div>
                                    ))}

                                    {/* Navigation Arrows */}
                                    {images.length > 1 && (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            plusSlides(-1, overlayIndex);
                                          }}
                                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50 transition-all"
                                          aria-label="Previous image"
                                        >
                                          <ChevronLeftIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            plusSlides(1, overlayIndex);
                                          }}
                                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50 transition-all"
                                          aria-label="Next image"
                                        >
                                          <ChevronRightIcon className="w-5 h-5" />
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-gray-400">No images available</div>
                                )}
                              </div>

                              {/* Thumbnail Dots */}
                              {images.length > 1 && (
                                <div className="flex justify-center gap-2 mt-2">
                                  {images.map((_, i) => (
                                    <button
                                      key={i}
                                      onClick={() => setCurrentSlides(prev => {
                                        const newSlides = [...prev];
                                        newSlides[overlayIndex] = i + 1;
                                        return newSlides;
                                      })}
                                      className={`w-3 h-3 rounded-full transition-colors ${currentSlides[overlayIndex] === i + 1 ? 'bg-orange-500' : 'bg-gray-300'
                                        }`}
                                      aria-label={`Go to slide ${i + 1}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Order Details - Right Side */}
                      <div className="lg:w-3/5 xl:w-2/3">
                        {/* Basic Order Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Order ID</p>
                            <p className="font-medium text-gray-900">{selectedOrder.order_id}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Quantity</p>
                            <p className="font-medium text-gray-900">{selectedOrder.quantity}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Fabric</p>
                            <p className="font-medium text-gray-900">{selectedOrder.fabric}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Color</p>
                            <p className="font-medium text-gray-900">{selectedOrder.color}</p>
                          </div>
                          <div className="md:col-span-2 bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-500 mb-1">Measurements</p>
                            <p className="font-medium text-gray-900">
                              {selectedOrder.size || selectedOrder.measurements || 'Not specified'}
                            </p>
                          </div>
                        </div>

                        {/* Additional Notes */}
                        {(selectedOrder.description || selectedOrder.note) && (
                          <div className="mb-6">
                            {selectedOrder.description && (
                              <div className="mb-4 bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-500 mb-1">Description</p>
                                <p className="font-medium text-gray-900">{selectedOrder.description}</p>
                              </div>
                            )}
                            {selectedOrder.note && (
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-500 mb-1">Note</p>
                                <p className="font-medium text-gray-900">{selectedOrder.note}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Customizations Section */}
                        {(selectedOrder.fab !== 'N/A' || selectedOrder.border !== 'N/A' || selectedOrder.borderdesign !== 'N/A') && (
                          <div className="border-t pt-6">
                            <h4 className="text-lg font-semibold mb-4 text-gray-800">Customizations</h4>
                            <div className="space-y-4">
                              {/* Fabric Customization */}
                              {selectedOrder.fab && selectedOrder.fab !== 'N/A' && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2">Custom Fabric</p>
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {selectedOrder.fab_img && (
                                      <div className="w-24 h-24 flex-shrink-0 bg-white p-1 border rounded overflow-hidden">
                                        <img
                                          src={selectedOrder.fab_img}
                                          alt="Custom fabric"
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                    )}
                                    <p className="text-gray-700 flex-1">{selectedOrder.fab}</p>
                                  </div>
                                </div>
                              )}

                              {/* Border Customization */}
                              {selectedOrder.border && selectedOrder.border !== 'N/A' && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2">Custom Border</p>
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {selectedOrder.border_img && (
                                      <div className="w-24 h-24 flex-shrink-0 bg-white p-1 border rounded overflow-hidden">
                                        <img
                                          src={selectedOrder.border_img}
                                          alt="Custom border"
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                    )}
                                    <p className="text-gray-700 flex-1">{selectedOrder.border}</p>
                                  </div>
                                </div>
                              )}

                              {/* Border Design Customization */}
                              {selectedOrder.borderdesign && selectedOrder.borderdesign !== 'N/A' && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                  <p className="font-semibold text-gray-700 mb-2">Border Design</p>
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {selectedOrder.border_design_img && (
                                      <div className="w-24 h-24 flex-shrink-0 bg-white p-1 border rounded overflow-hidden">
                                        <img
                                          src={selectedOrder.border_design_img}
                                          alt="Border design"
                                          className="w-full h-full object-contain"
                                        />
                                      </div>
                                    )}
                                    <p className="text-gray-700 flex-1">{selectedOrder.borderdesign}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
    />
  );
};

export default OrdersPage;
